/* eslint-disable @typescript-eslint/naming-convention */
import './styles.scss';

import React, {
  useCallback,
  useMemo,
  useState,
} from 'react';

import { Typography } from 'antd';
// eslint-disable-next-line import/no-unresolved
import EasyVirtualizedScroller from 'easy-react-virtualized';
import { IoChevronDown } from 'react-icons/io5';

import UserAPI from '../../../../api/UserAPI';
import Header from '../../../../components/Header/Header';
import Loader from '../../../../components/Loader';
import { useTheme } from '../../../../context/ThemeProvider';
import showAppError from '../../../../shared/error';
import { useAppNavigate } from '../../../../shared/hooks';
import { ISubscription } from '../../../../types/userTypes';
import NoDataFound from '../../../Feed/components/NoDataFound/NoDataFound';
import Subscription from '../../components/Subscription';
import SubscriptionDetails from './SubscriptionDetails';

const purchaseHistoryTitle = 'Purchase History';

const subscriptionDetailsTitle = 'Subscription Details';

type TSUB_TYPES = 'Active' | 'Expired';
const SUB_TYPES: TSUB_TYPES[] = ['Active', 'Expired'];

const Subscriptions: React.FC = () => {
  const { colors } = useTheme();
  const navigate = useAppNavigate();

  const [subscriptions, setSubscriptions] = useState<ISubscription[]>([]);
  const [selectedSubType, setSelectedSubType] = useState<TSUB_TYPES>('Active');
  const [loading, setLoading] = React.useState(false);
  const [selectedSubscriptionDetails, setSelectedSubscriptionDetails] =
    useState<ISubscription | null>(null);

  const loadData = async () => {
    try {
      setLoading(true);
      const resp = await UserAPI.getAllSubscriptions();
      if (resp.status === 200) {
        setSubscriptions(resp.data.result);
      } else {
        showAppError(resp.data);
      }
    } catch (err) {
      showAppError(err);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getTagItem = useCallback(
    (
      value: string,
      key: string,
      onClick: () => void,
      isSelected: boolean,
      hasArrow: boolean = false,
    ) => (
      <div
        key={key}
        onClick={onClick}
        className={`tags-filter__tag ${isSelected ? 'active' : ''}`}>
        <Typography.Text>{value}</Typography.Text>
        {hasArrow && (
          <IoChevronDown
            size={18}
            color={isSelected ? colors.PRIMARY : colors.ICON}
          />
        )}
      </div>
    ),
    [colors.ICON, colors.PRIMARY],
  );

  const filteredSubscriptions = useMemo(() => {
    if (selectedSubType === 'Active') {
      return subscriptions.filter(
        (subscription) =>
          subscription.status === 'active' ||
          subscription.status === 'manualactive',
      );
    } else {
      return subscriptions.filter(
        (subscription) =>
          subscription.status === 'expired' ||
          subscription.status === 'manualexpired',
      );
    }
  }, [selectedSubType, subscriptions]);

  const pageTitle = useMemo(() => {
    if (selectedSubscriptionDetails) {
      return subscriptionDetailsTitle;
    }
    return purchaseHistoryTitle;
  }, [selectedSubscriptionDetails]);

  const displaySubscriptionListAndDetails = useMemo(() => {
    if (filteredSubscriptions.length === 0 && loading) {
      return (
        <div
          style={{
            width: '100%',
            textAlign: 'center',
            margin: '45px 0',
          }}>
          {loading ? (
            <Loader size="large" />
          ) : (
            <NoDataFound title="No subscriptions found!" />
          )}
        </div>
      );
    }

    if (selectedSubscriptionDetails) {
      return <SubscriptionDetails subscription={selectedSubscriptionDetails} />;
    }
    return (
      <EasyVirtualizedScroller hasMore={false} useParentScrollElement>
        {filteredSubscriptions.map((item: ISubscription) => (
          <Subscription
            key={item._id}
            subscription={item}
            onViewFlexiDetails={setSelectedSubscriptionDetails}
            reloadSubscriptions={() => {
              setSubscriptions([]);
              loadData();
            }}
          />
        ))}
      </EasyVirtualizedScroller>
    );
  }, [filteredSubscriptions, loading, selectedSubscriptionDetails]);

  return (
    <div className="pageRoot">
      <Header
        title={pageTitle}
        handleBack={() => {
          if (selectedSubscriptionDetails) {
            setSelectedSubscriptionDetails(null);
          } else {
            navigate.goBack();
          }
        }}
      />
      {!selectedSubscriptionDetails ? (
        <div className="tags-filter__container" style={{ borderTop: 'none' }}>
          <div className="tags-filter__wrapper">
            {SUB_TYPES.map((subType, index) =>
              getTagItem(
                subType,
                subType + index,
                () => setSelectedSubType(subType),
                subType === selectedSubType,
              ),
            )}
          </div>
        </div>
      ) : null}
      <div className="pageContent subscriptionList pageContentList">
        {displaySubscriptionListAndDetails}
      </div>
    </div>
  );
};

export default Subscriptions;
